/* The three group reports: catalogue, run and CSV export.
   Hand-typed until the records lane's schema.d.ts regenerates these (m4-rulings.md #59). */

import { useMutation, useQuery, type UseQueryResult } from "@tanstack/react-query";
import { apiBlob, apiGet, apiSend } from "./client";

export type ReportParamType = "date" | "entities" | "select";

export type ReportParam = {
  name: string;
  type: ReportParamType;
  required: boolean;
  values?: string[];
};

export type ReportDefinition = {
  key: string;
  name: string;
  description: string;
  tag: string;
  params: ReportParam[];
};

export type ReportColumn = { key: string; label: string; type: string };
export type ReportResult = { columns: ReportColumn[]; rows: unknown[][]; generatedAt: string };

/** What the form collects; `from` and `to` are always required (m4-rulings.md #59). */
export type ReportParams = { from: string; to: string; entityIds: string[]; owner: string };

export function emptyParams(): ReportParams {
  return { from: "", to: "", entityIds: [], owner: "" };
}

/** Only the params a report declares are sent, so the body matches its own contract. */
export function reportBody(
  definition: ReportDefinition,
  params: ReportParams,
): Record<string, unknown> {
  const body: Record<string, unknown> = {};
  for (const param of definition.params) {
    if (param.name === "entityIds") body.entityIds = params.entityIds;
    else if (param.name === "owner") {
      if (params.owner) body.owner = params.owner;
    } else {
      const value = params[param.name as "from" | "to"];
      if (value) body[param.name] = value;
    }
  }
  return body;
}

export function reportQuery(definition: ReportDefinition, params: ReportParams): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(reportBody(definition, params))) {
    if (Array.isArray(value)) for (const item of value) search.append(key, String(item));
    else search.set(key, String(value));
  }
  const query = search.toString();
  return query ? `?${query}` : "";
}

function isSet(param: ReportParam, params: ReportParams): boolean {
  if (param.type === "entities") return params.entityIds.length > 0;
  if (param.type === "select") return params.owner !== "";
  return params[param.name as "from" | "to"] !== "";
}

/** The names of the required params this report is still missing. */
export function missingParams(definition: ReportDefinition, params: ReportParams): string[] {
  return definition.params.filter((param) => param.required && !isSet(param, params)).map((p) => p.name);
}

export function useReports(): UseQueryResult<ReportDefinition[]> {
  return useQuery({
    queryKey: ["reports"],
    queryFn: async () => (await apiGet<{ items: ReportDefinition[] }>("/api/reports")).items,
  });
}

export function useRunReport(key: string) {
  return useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      apiSend<ReportResult>("POST", `/api/reports/${key}/run`, body),
  });
}

export function useReportExport(key: string) {
  return useMutation({
    mutationFn: (query: string) => apiBlob(`/api/reports/${key}/export.csv${query}`),
  });
}
